import { ContentState } from "draft-js";
import { I18n } from "@assets/strings";
import { Set } from "immutable";
import React, { useState } from "react";
import styled from "styled-components";
import { QuizDraft } from "@domain/lms/quiz/quizDraft";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { DraftRenderer } from "@ui/components/containers/draftRenderer";
import { Secondary } from "@ui/components/containers/textBlocks";
import { ExerciseView } from "@ui/components/course/exercise/exerciseView";
import { RichTextInput } from "@ui/components/inputs/draft/richTextInput";
import { Toggle } from "@ui/components/inputs/toggle";
import { stack, Stack } from "@ui/components/layout";
import { ClosableModal } from "@ui/modals/closableModal";
import { gap } from "@ui/styles/utils";

interface PublishQuizModalProps extends React.HTMLAttributes<HTMLDivElement> {
	onConfirm: (confirmed: boolean, shouldShare: boolean, shareMessage?: ContentState) => void;
	quiz: QuizDraft;
}
export const PublishQuizModal: React.FC<PublishQuizModalProps> = ({ quiz, onConfirm, ...htmlProps }) => {
	const [shouldShare, setShouldShare] = useState(true);
	const [shareMessage, setShareMessage] = useState(ContentState.createFromText(""));
	return (
		<Container
			close={() => {
				onConfirm(false, false);
			}}
			title={I18n.t("quiz.editor.publish_modal.title")}
			lightTitle
			{...htmlProps}
		>
			<Stack gap={10}>
				<Title>{quiz.title}</Title>
				<DraftRenderer content={quiz.description} />
			</Stack>
			{quiz.exercises.map((ex, i) => (
				<ExerciseView
					key={ex.id}
					exercise={ex}
					index={i}
					currentAnswer={Set([ex.choices.findIndex((c) => !!c.valid)])}
					preview
				/>
			))}
			<Share>
				<Toggle toggled={shouldShare} onChange={setShouldShare} />
				<ShareText>{I18n.t("course.editor.publish_modal.share")}</ShareText>
			</Share>
			{shouldShare && (
				<StyledInput
					mentionsEnabled
					richStyleEnabled={false}
					placeholder={I18n.t("course.editor.publish_modal.share_placeholder")}
					value={shareMessage}
					onChange={(v) => setShareMessage(v)}
				/>
			)}
			<PrimaryButton
				onClick={() => onConfirm(true, shouldShare, shareMessage.getPlainText() ? shareMessage : undefined)}
			>
				{I18n.t("quiz.editor.publish_modal.submit")}
			</PrimaryButton>
		</Container>
	);
};

const Container = styled(ClosableModal)`
	${stack(16, "center", "stretch")};

	button {
		margin-top: 44px;
	}
`;

const Title = styled.div`
	${({ theme }) => theme.textStyles.title};
	margin: 20px 0 35px;
	text-align: center;
`;

const Share = styled.div`
	margin-top: 44px;
	display: flex;
	align-items: center;
	${gap(12, "row")};

	> :first-child {
		flex-shrink: 0;
	}
`;

const ShareText = styled(Secondary)`
	text-align: initial;
	max-width: 220px;
`;

const StyledInput = styled(RichTextInput)`
	width: 100%;
	border: 1px solid ${({ theme }) => theme.colors.lightergray};
	border-radius: 16px;

	.public-DraftEditor-content,
	.public-DraftEditorPlaceholder-root {
		padding: 10px 12px 9px;
		font-size: 0.8125rem;
	}
	.public-DraftEditorPlaceholder-root {
		font-weight: 400;
		opacity: 0.8;
	}
`;
